import { FC, useMemo } from 'react';

import { ChatBubbleOutline, FlagCircle, CheckCircle, LockClock, Beenhere } from '@mui/icons-material';
import { Box, Card, Divider, Typography, Chip, Theme, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router';

import PGX from '../../../assets/PGXIcon.png';
import { useAnalytics } from '../../../hooks/dataTracking/useAnalytics';
import { Clock } from '../../../icons/clock';
import { IProposal, EProposalStatus } from '../../../interfaces/proposal';
import { abbreviateNumber, getRemainingDuration } from '../../../utils/humanizer';
import { StatusChip } from '../../molecules/StatusChip';
import { UserTag } from '../../molecules/UserTag';

type ProposalStatus = EProposalStatus.Active | EProposalStatus.Closed | EProposalStatus.Pending;

export interface ProposalCardProps extends IProposal {
  complete?: boolean;
  coreInteraction?: boolean;
}

const StatusTimeText = ({
  status,
  endDate,
  outcome,
  startDate,
}: {
  status?: ProposalStatus;
  endDate?: Date;
  outcome?: string;
  startDate?: Date;
}) => {
  switch (status) {
    case 'Active':
      return endDate ? (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 0 }}>
          <Clock color="secondary" />
          <Typography variant={'body2'} sx={{ pl: 1 }}>
            Ends in {getRemainingDuration(endDate)}
          </Typography>
        </Box>
      ) : null;

    case 'Pending':
      return startDate ? (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <LockClock color="warning" />
          <Typography variant={'body2'} sx={{ pl: 1 }}>
            Starts in {getRemainingDuration(startDate)}
          </Typography>
        </Box>
      ) : null;
    case 'Closed':
      if (outcome === 'No Result')
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FlagCircle color="primary" />
            <Typography variant={'body2'} sx={{ pl: 1 }}>
              {outcome}
            </Typography>
          </Box>
        );
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <CheckCircle color="secondary" />
          <Typography variant={'body2'} sx={{ pl: 1 }}>
            {outcome}
          </Typography>
        </Box>
      );

    default:
      return null;
  }
};

export const ProposalCard: FC<ProposalCardProps> = ({
  _id,
  title,
  comments,
  complete,
  feedback,
  summary,
  endDate,
  voters,
  status,
  outcome,
  creatorAddress,
  startDate,
}) => {
  const theme = useTheme();
  const { sendButtonClickTrackingEvent } = useAnalytics();
  const navigate = useNavigate();
  const mdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'), {
    noSsr: true,
  });

  const upvotersNetWorth = useMemo(() => voters?.reduce((acc, obj) => acc + obj.netWorth, 0) || 0, [_id]);

  return (
    <Card
      sx={{ boxShadow: 10, display: 'flex', background: 'none', flexDirection: { xs: 'column-reverse', md: 'row' } }}
    >
      <Box
        sx={{ p: 3, width: '100%', background: theme.palette.background.paper, cursor: complete ? '' : 'pointer' }}
        onClick={() => {
          navigate(`/proposals/${_id}`);
          sendButtonClickTrackingEvent(`Navigate to proposal ID ${_id}`)
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 1.5 }}>
          <UserTag publicAddress={creatorAddress} />
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {feedback &&
              <Chip
                sx={{ mr: 1 }}
                icon={<><Beenhere color="primary" sx={{ mr: { xs: -1.5, md: -0.5 }, ml: 0.5, fontSize: "18px" }} /></>}
                size="small"
                label={
                  mdUp ? (
                    <Typography color="primary"
                      variant="caption"
                      fontWeight="bold" >
                      PEGAXY TEAM FEEDBACK
                    </Typography>
                  ) : ''
                }
              />
            }
            {status && <StatusChip status={status} />}
          </Box>
        </Box>

        <Typography variant={'h6'}>{title}</Typography>
        <Typography
          sx={
            complete
              ? { my: 2 }
              : { my: 2, display: '-webkit-box', WebkitLineClamp: 4, overflow: 'hidden', WebkitBoxOrient: 'vertical' }
          }
          color="text.secondary"
        >
          {summary}
        </Typography>
        <Divider />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 3 }}>
          <StatusTimeText status={status} endDate={endDate} outcome={outcome} startDate={startDate} />

          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'nowrap', ml: 1 }}>
            <Box sx={{ width: 20, height: 20, marginRight: 1 }}>
              <img style={{ maxWidth: '100%' }} src={PGX} alt="" />
            </Box>
            <Typography variant={'body2'}>{abbreviateNumber(upvotersNetWorth)}</Typography>
            <Divider orientation="vertical" sx={{ height: '20px', mx: 2 }} />
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ChatBubbleOutline fontSize="small" sx={{ color: 'neutral.400' }} />
              <Typography sx={{ ml: 1 }} variant="body2" color="neutral.400">
                {comments?.length ? (comments?.length > 50 ? '50+' : comments?.length) : '0'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Card>
  );
};
